export function returnListingToReview(token: string, id: number) {
  return adminFetch<{ success: boolean; listing_status: string; searchSyncPending?: boolean }>(`/admin/listings/${id}/return-to-review`, token, { method: "POST" }, false);
}
import { adminFetch } from "./_client";
import type { ListingDiffResponse, ListingTrustResponse, ListingPhotoQualityResponse, VehicleLifecycleState, VehicleLifecycleReason, CarLifecycle, CarLifecycleEvent } from "./types";

export function fetchListingDiff(token: string, id: number) {
  return adminFetch<ListingDiffResponse>(`/admin/listings/${id}/diff`, token);
}

export function fetchListingTrustSignals(token: string, id: number) {
  return adminFetch<ListingTrustResponse>(`/admin/listings/${id}/trust-signals`, token);
}

export function fetchListingPhotoQuality(token: string, id: number) {
  return adminFetch<ListingPhotoQualityResponse>(`/admin/listings/${id}/photo-quality`, token);
}


export function requestBetterListingPhotos(token: string, id: number, opts: { template_slug?: string; locale?: string; message?: string; mark_incomplete?: boolean } = {}) {
  return adminFetch<{ success: boolean; owner_notified: boolean; message: string; template_id: number | null }>(
    `/admin/listings/${id}/request-better-photos`,
    token,
    { method: "POST", body: JSON.stringify(opts) },
  );
}


// Admin removal of an offending photo during moderation review. Soft-deletes
// via the same path the host-facing delete uses; the server refuses (409
// `last_active_photo`) to remove the only active photo of a live listing.
export function removeListingPhoto(token: string, id: number, photoId: number) {
  return adminFetch<{ success: boolean; was_primary: boolean }>(
    `/admin/listings/${id}/photos/${photoId}`,
    token,
    { method: "DELETE" },
  );
}


export const VEHICLE_LIFECYCLE_REASON_CODES: Record<VehicleLifecycleState, VehicleLifecycleReason[]> = {
  listed: [
    { code: "reactivated", label: "Reactivated", description: "Owner or admin brought the listing back live" },
    { code: "snooze_lifted", label: "Snooze lifted", description: "Temporary pause is over" },
  ],
  snoozed: [
    { code: "owner_holiday", label: "Owner away", description: "Owner is on holiday or temporarily unavailable" },
    { code: "maintenance", label: "Vehicle in maintenance", description: "Workshop / repair pause" },
    { code: "seasonal_off", label: "Seasonal pause", description: "Off-season, not available right now" },
    { code: "low_demand", label: "Low demand", description: "Pulled while we work on listing quality" },
    { code: "insurance_pending", label: "Insurance pending", description: "Waiting on insurance documents" },
  ],
  delisted: [
    { code: "owner_request", label: "Owner request", description: "Owner asked to remove the listing" },
    { code: "policy_violation", label: "Policy violation", description: "Failed our content / safety policy" },
    { code: "repeated_complaints", label: "Repeated complaints", description: "Multiple guest complaints" },
    { code: "fraud_suspected", label: "Fraud suspected", description: "Suspicious activity flagged by trust & safety" },
    { code: "payments_issue", label: "Payments issue", description: "Outstanding payouts / refunds" },
    { code: "duplicate_listing", label: "Duplicate listing", description: "Same vehicle listed twice" },
  ],
  archived: [
    { code: "stale_long_term", label: "Stale long-term", description: "No activity in 12+ months" },
    { code: "owner_account_closed", label: "Owner account closed", description: "Owner deleted their account" },
    { code: "vehicle_sold", label: "Vehicle sold", description: "Owner sold the car" },
    { code: "permanently_removed", label: "Permanently removed", description: "Manual archival" },
  ],
};


export function fetchListingLifecycle(token: string, id: number) {
  return adminFetch<{ current: CarLifecycle | null; history: CarLifecycleEvent[] }>(
    `/admin/listings/${id}/lifecycle`,
    token,
  );
}


export function setListingLifecycle(
  token: string,
  id: number,
  body: { state: VehicleLifecycleState; reason_code: string; reason_note?: string },
) {
  return adminFetch<{ current: CarLifecycle; searchSyncPending?: boolean; metadataPending?: boolean }>(
    `/admin/listings/${id}/lifecycle`,
    token,
    { method: "POST", body: JSON.stringify(body) },
    false,
  );
}
